import { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import {
  PERMISSION_CACHE_KEY,
  clearPermissionCache,
  flattenUserPermissions,
  getPermissionTitle,
} from '../Utilis/permissionHelpers';

export interface CachedPermission {
  id: string;
  title: string;
  module: string;
  action: string;
}

export interface CachedRole {
  id: number;
  name: string;
  permissions: CachedPermission[];
}

export interface CachedUserPermissions {
  roles: CachedRole[];
  permissions: CachedPermission[];
  userType: number;
  isAdmin: boolean;
  lastUpdated: number;
}

const CACHE_DURATION = 30 * 60 * 1000; // 30 minutes

export const usePermissionCache = () => {
  const { userInfo } = useSelector((state: any) => state.setReducer);
  const [cachedPermissions, setCachedPermissions] =
    useState<CachedUserPermissions | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const loadFromCache = useCallback((): CachedUserPermissions | null => {
    try {
      const cached = localStorage.getItem(PERMISSION_CACHE_KEY);
      if (!cached) return null;

      const parsed: CachedUserPermissions = JSON.parse(cached);
      const now = Date.now();

      if (now - parsed.lastUpdated > CACHE_DURATION) {
        clearPermissionCache();
        return null;
      }

      return parsed;
    } catch (error) {
      console.error('Error loading permissions from cache:', error);
      clearPermissionCache();
      return null;
    }
  }, []);

  const saveToCache = useCallback((permissions: CachedUserPermissions) => {
    try {
      const cacheData = {
        ...permissions,
        lastUpdated: Date.now(),
      };
      localStorage.setItem(PERMISSION_CACHE_KEY, JSON.stringify(cacheData));
    } catch (error) {
      console.error('Error saving permissions to cache:', error);
    }
  }, []);

  const clearCache = useCallback(() => {
    clearPermissionCache();
    setCachedPermissions(null);
  }, []);

  // Always rebuild from current userInfo when available — never prefer a stale
  // localStorage snapshot over Redux auth/me data (that kept removed permissions
  // visible in the sidebar after role changes).
  const getPermissions = useCallback((): CachedUserPermissions | null => {
    if (!userInfo) {
      return loadFromCache();
    }

    const roles = userInfo.roles || [];
    const allPermissions = flattenUserPermissions(userInfo);

    const permissionData: CachedUserPermissions = {
      roles,
      permissions: allPermissions,
      userType: userInfo.type ?? -1,
      isAdmin: !!(userInfo.isAdmin || userInfo.type === 0),
      lastUpdated: Date.now(),
    };

    saveToCache(permissionData);
    return permissionData;
  }, [userInfo, loadFromCache, saveToCache]);

  const hasPermission = useCallback(
    (permissionTitle: string): boolean => {
      if (!cachedPermissions) return false;
      if (cachedPermissions.isAdmin) return true;
      if (!permissionTitle) return false;

      return cachedPermissions.permissions.some(
        (permission) => getPermissionTitle(permission) === permissionTitle
      );
    },
    [cachedPermissions]
  );

  const hasAnyPermission = useCallback(
    (permissionTitles: string[]): boolean => {
      if (!cachedPermissions) return false;
      if (cachedPermissions.isAdmin) return true;

      const validTitles = permissionTitles.filter(
        (title): title is string => typeof title === 'string' && title.length > 0
      );
      if (validTitles.length === 0) return false;

      return validTitles.some((title) => hasPermission(title));
    },
    [cachedPermissions, hasPermission]
  );

  const hasAllPermissions = useCallback(
    (permissionTitles: string[]): boolean => {
      if (!cachedPermissions) return false;
      if (cachedPermissions.isAdmin) return true;

      const validTitles = permissionTitles.filter(
        (title): title is string => typeof title === 'string' && title.length > 0
      );
      if (validTitles.length === 0) return false;

      return validTitles.every((title) => hasPermission(title));
    },
    [cachedPermissions, hasPermission]
  );

  const can = useCallback(
    (module: string, action: string): boolean => {
      const permissionTitleWithManage = `manage_${module}_${action}`;
      const permissionTitleWithoutManage = `${module}_${action}`;

      return (
        hasPermission(permissionTitleWithManage) ||
        hasPermission(permissionTitleWithoutManage)
      );
    },
    [hasPermission]
  );

  const canView = useCallback(
    (module: string): boolean => can(module, 'view'),
    [can]
  );
  const canCreate = useCallback(
    (module: string): boolean => can(module, 'create'),
    [can]
  );
  const canEdit = useCallback(
    (module: string): boolean => can(module, 'edit'),
    [can]
  );
  const canDelete = useCallback(
    (module: string): boolean => can(module, 'delete'),
    [can]
  );
  const canShow = useCallback(
    (module: string): boolean => can(module, 'show'),
    [can]
  );

  const hasRole = useCallback(
    (roleIds: number[]): boolean => {
      if (!cachedPermissions) return false;
      if (cachedPermissions.isAdmin) return true;

      return cachedPermissions.roles.some((role) => roleIds.includes(role.id));
    },
    [cachedPermissions]
  );

  const getModulePermissions = useCallback(
    (module: string): CachedPermission[] => {
      if (!cachedPermissions) return [];

      return cachedPermissions.permissions.filter(
        (permission) => permission.module === module
      );
    },
    [cachedPermissions]
  );

  const canAccessRoute = useCallback(
    (routePermissions: string[]): boolean => {
      if (!cachedPermissions) return false;
      if (cachedPermissions.isAdmin) return true;

      return hasAnyPermission(routePermissions);
    },
    [cachedPermissions, hasAnyPermission]
  );

  const refreshPermissions = useCallback(() => {
    clearCache();
    const permissions = getPermissions();
    setCachedPermissions(permissions);
  }, [clearCache, getPermissions]);

  useEffect(() => {
    if (userInfo) {
      setIsLoading(true);
      clearPermissionCache();
      const permissions = getPermissions();
      setCachedPermissions(permissions);
      setIsLoading(false);
    } else {
      setCachedPermissions(null);
    }
  }, [userInfo, getPermissions]);

  return {
    cachedPermissions,
    isLoading,

    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    can,
    canView,
    canCreate,
    canEdit,
    canDelete,
    canShow,

    hasRole,

    getModulePermissions,
    canAccessRoute,

    refreshPermissions,
    clearCache,
    loadFromCache,
    saveToCache,
  };
};

export default usePermissionCache;
